/* eslint-disable @typescript-eslint/prefer-for-of */
import { useState, useCallback } from 'react';
import { getRandomRows } from './functions';
import { TileProps, defaultGameConfig, GameConfigProps } from './GameConfigContext';

export interface HookProps {
  gameConfig: GameConfigProps;
  setGameConfig: React.Dispatch<React.SetStateAction<GameConfigProps>>;
  createTiles: (gameConfigObj?: GameConfigProps) => TileProps[][];
  showTileByXY: (x: number, y: number, copyTiles: TileProps[][]) => { tiles: TileProps[][], gameOver: boolean, flagCount: number } | TileProps[][];
  setFlagged: (x: number, y: number, flag: boolean) => void;
  loadNewGame: () => void;
  markAllAround: (x: number, y: number, mark: boolean) => void;
  tileDoubleClicked: (x: number, y: number) => void;
}

const useGameConfig = (): HookProps => {
  const [gameConfig, setGameConfigState] = useState(defaultGameConfig);
  const setGameConfig = useCallback((_gameConfig: GameConfigProps) => {
    setGameConfigState(_gameConfig);
  }, []);

  const createTiles = (gameConfigObj?: GameConfigProps, exclude?: {x: number; y: number}) => {
    const copyTiles: TileProps[][] = [];
    const gc = gameConfigObj ? gameConfigObj : gameConfig;
    const ex = exclude ? (exclude.x * gc.areaTilesWidthNum) + exclude.y : -1;
    const bombRows = getRandomRows(gc.areaTilesWidthNum * gc.areaTilesHeightNum, gc.bombCount, ex);

    for (let i = 0; i < gc.areaTilesHeightNum; i++) {
      if (!copyTiles[i]) copyTiles[i] = [];
      for (let j = 0; j < gc.areaTilesWidthNum; j++) {
        const currentNumRow = (i * gc.areaTilesWidthNum) + j;
        const isBomb = bombRows.indexOf(currentNumRow) >= 0;
        const tile: TileProps = {
          hidden: true,
          bomb: isBomb,
          bombsNear: isBomb ? -1 : 0,
          flagged: false,
        }
        copyTiles[i][j] = tile;
      }
    }

    for (let x = 0; x < gc.areaTilesHeightNum; x++) {
      for (let y = 0; y < gc.areaTilesWidthNum; y++) {
        let countBombsNear = 0;
        if (copyTiles[x-1] && copyTiles[x-1][y-1]) {
          if (copyTiles[x-1][y-1].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x] && copyTiles[x][y-1]) {
          if (copyTiles[x][y-1].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x+1] && copyTiles[x+1][y-1]) {
          if (copyTiles[x+1][y-1].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x-1] && copyTiles[x-1][y]) {
          if (copyTiles[x-1][y].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x+1] && copyTiles[x+1][y]) {
          if (copyTiles[x+1][y].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x] && copyTiles[x][y+1]) {
          if (copyTiles[x][y+1].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x-1] && copyTiles[x-1][y+1]) {
          if (copyTiles[x-1][y+1].bomb) {
            countBombsNear++;
          }
        }
        if (copyTiles[x+1] && copyTiles[x+1][y+1]) {
          if (copyTiles[x+1][y+1].bomb) {
            countBombsNear++;
          }
        }

        if (!copyTiles[x][y].bomb) {
          copyTiles[x][y].bombsNear = countBombsNear;
        }
      }
    }
    return copyTiles;
    // setGameConfig({...gc, tiles: copyTiles});
  };

  const setFlagged = (x: number, y: number, flag: boolean) => {
    const copyTiles = [...gameConfig.tiles];
    copyTiles[x][y].flagged = flag;
    setGameConfig({...gameConfig, tiles: copyTiles});
  }

  const showRows: {x: number; y: number}[] = [];
  const showTileByXY2 = (x: number, y: number, tiles: TileProps[][]): void => {
    if (tiles[x][y].bombsNear === 0) {
      for (let xx = Math.max(x-1, 0); xx <= Math.min(x+1, gameConfig.areaTilesHeightNum - 1); xx++) {
        for(let yy = Math.max(y-1, 0); yy <= Math.min(y+1, gameConfig.areaTilesWidthNum - 1); yy++) {
          if (xx === x && yy === y) continue;
          if (showRows.findIndex((row) => row.x === xx && row.y === yy) === -1) {
            showRows.push({x: xx, y: yy});
            showTileByXY2(xx, yy, tiles);
          }
        }
      }
    }
  }

  const showTileByXY = (x: number, y: number, copyTiles: TileProps[][], deep = 0): { tiles: TileProps[][], gameOver: boolean, flagCount: number } => {
    copyTiles[x][y].hidden = false;

    const output: { tiles: TileProps[][], gameOver: boolean, flagCount: number } = {
      gameOver: false,
      tiles: [],
      flagCount: gameConfig.flagCount,
    }

    // Jeżeli trafiono na bombę
    if (copyTiles[x][y].bomb) {
      // Odkryj wszystkie bomby na boardzie
      output.tiles = copyTiles.map((tile:TileProps[]) => tile.map((til:TileProps) => {
        return til.bomb ? {...til, hidden: false} : til;
      }));
      // Ustaw game over
      output.gameOver = true;
      return output;
    }

    showRows.splice(0, showRows.length);
    showTileByXY2(x, y, copyTiles);

    // console.log(showRows);

    for (const row of showRows) {
      copyTiles[row.x][row.y].hidden = false;
      if (copyTiles[row.x][row.y].flagged) {
        output.flagCount--;
      }
    }

    output.tiles = copyTiles;
    return output;
  }

  const loadNewGame = () => {
    const newGame = {...gameConfig};
    newGame.correctCount = 0;
    newGame.flagCount = 0;
    newGame.gameOver = false;
    newGame.win = false;
    newGame.started = false;
    newGame.tiles = [];
    newGame.reloaded = !newGame.reloaded;
    newGame.tiles = createTiles(newGame);
    setGameConfig(newGame);
  }

  const markAllAround = (x: number, y: number, mark: boolean) => {
    for (let xx = Math.max(x-1, 0); xx <= Math.min(x+1, gameConfig.areaTilesHeightNum - 1); xx++) {
      for(let yy = Math.max(y-1, 0); yy <= Math.min(y+1, gameConfig.areaTilesWidthNum - 1); yy++) {
        gameConfig.tiles[xx][yy].isMouseDown = mark;
      }
    }

    // console.log('set tiles mark on = ' + mark.toString());

    setGameConfig({...gameConfig });
  }

  const tileDoubleClicked = (x: number, y: number) => {
    let allBombsHaveFlag = true;
    for (let xx = Math.max(x-1, 0); xx <= Math.min(x+1, gameConfig.areaTilesHeightNum - 1); xx++) {
      for(let yy = Math.max(y-1, 0); yy <= Math.min(y+1, gameConfig.areaTilesWidthNum - 1); yy++) {
        // sprawdz czy wszystkie bomby maja na sobie flage, jesli tak odslon tile'y
        if (gameConfig.tiles[xx][yy].bomb === true && gameConfig.tiles[xx][yy].flagged === false)
        {
          allBombsHaveFlag = false;
        }
        if (gameConfig.tiles[xx][yy].bomb === false && gameConfig.tiles[xx][yy].flagged === true) {
          gameConfig.tiles = gameConfig.tiles.map((tile:TileProps[]) => tile.map((til:TileProps) => {
            return til.bomb ? {...til, hidden: false} : til;
          }));
          // Ustaw game over
          gameConfig.gameOver = true;
          setGameConfig({...gameConfig, tiles: gameConfig.tiles, gameOver: true, win: false });
          return;
        }
      }
    }
    if (allBombsHaveFlag)
    {
      for (let xx = Math.max(x-1, 0); xx <= Math.min(x+1, gameConfig.areaTilesHeightNum - 1); xx++) {
        for(let yy = Math.max(y-1, 0); yy <= Math.min(y+1, gameConfig.areaTilesWidthNum - 1); yy++) {
          // sprawdz czy wszystkie bomby maja na sobie flage, jesli tak odslon tile'y
          if (gameConfig.tiles[xx][yy].flagged === false)
          {
            gameConfig.tiles[xx][yy].hidden = false;
            if (gameConfig.tiles[xx][yy].bombsNear === 0) {
              const res = showTileByXY(xx, yy, gameConfig.tiles);
              gameConfig.tiles = res.tiles;
            }
          }
        }
      }
      setGameConfig({...gameConfig, tiles: gameConfig.tiles});
    }
  }

  return {
    gameConfig,
    setGameConfig,
    createTiles,
    showTileByXY,
    setFlagged,
    loadNewGame,
    tileDoubleClicked,
    markAllAround
  };
};

export default useGameConfig;
